import { conceptoTareoAplicaACanal } from './ajusteConceptoDestino';

/** Códigos PLAME de gratificación (iguales a liquidaciones). */
export const CODIGO_PLAME_GRATIFICACION = '0406';
/** Bonificación extraordinaria proporcional Ley (código operativo en liquidaciones). */
export const CODIGO_PLAME_BONIF_EXTRAORDINARIA = '0313';
/** Variante legacy / catálogo cercano (aparece en algunas planillas). */
export const CODIGO_PLAME_BONIF_EXTRAORDINARIA_ALT = '0312';

export const CODIGOS_GRATIFICACION_PLAME = [
  CODIGO_PLAME_GRATIFICACION,
  CODIGO_PLAME_BONIF_EXTRAORDINARIA,
  CODIGO_PLAME_BONIF_EXTRAORDINARIA_ALT,
];

function textoConceptoPlano(concepto) {
  return String(
    concepto?.descripcion
      || concepto?.concepto
      || concepto?.nombre
      || concepto?.plame_configuracion?.descripcion
      || ''
  )
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Concepto de liquidación en planilla (sí va en boleta ordinaria).
 */
export function esConceptoLiquidacionPlanilla(concepto) {
  if (!concepto) return false;
  const origen = concepto.origenCalculo || concepto.origen_calculo;
  return origen === 'liquidacion';
}

/**
 * Viático que sí se paga en boleta (no es el 0917 informativo).
 * En planillas cerradas a veces llega con excluir/origen viatico por error.
 */
export function esConceptoViaticoPagado(concepto) {
  if (!concepto) return false;
  const desc = textoConceptoPlano(concepto);
  return desc.includes('viatico') && desc.includes('pagado');
}

/**
 * Declaración de viáticos (tareo / viáticos standard): se informa en planilla/REM
 * pero no afecta el neto a pagar / boleta ordinaria.
 * Código habitual PLAME: 0917 CONDICIONES DE TRABAJO.
 */
export function esConceptoDeclaracionViatico(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return false;
  if (esConceptoViaticoPagado(concepto)) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || concepto.origen || '');
  if (origen === 'viatico' || origen === 'viatico_declaracion' || origen.includes('viatico')) {
    return true;
  }
  const codigo = String(
    concepto.codigoPlame
      || concepto.codigo
      || concepto.codigo_plame
      || concepto.plame_configuracion?.codigo_plame
      || ''
  );
  // 0917 = CONDICIONES DE TRABAJO (viáticos standard)
  if (codigo === '0917') return true;
  if (textoConceptoPlano(concepto).includes('condiciones de trabajo')) return true;
  return false;
}

/**
 * Declaración de gratificaciones / PLAME (módulo de gratificaciones).
 * No usar solo el flag excluir: en planilla cerrada los viáticos pagados
 * también vienen con excluir y no son grati.
 */
export function esConceptoDeclaracionGratificacion(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return false;
  if (esConceptoViaticoPagado(concepto)) return false;
  if (esConceptoDeclaracionViatico(concepto)) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || '').toLowerCase();
  if (origen === 'gratificacion' || origen === 'gratificacion_declaracion' || origen.includes('gratificacion')) {
    return true;
  }
  const codigo = String(
    concepto.codigoPlame
      || concepto.codigo
      || concepto.codigo_plame
      || concepto.plame_configuracion?.codigo_plame
      || ''
  );
  if (!CODIGOS_GRATIFICACION_PLAME.includes(codigo)) return false;
  return Boolean(concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria);
}

/** Movilidad 0909 que trajo Jatsa (consideraciones destino PLAME). No es la 0909 de tareo ni CONSU. */
export const CODIGO_PLAME_MOVILIDAD_JATSA = '0909';
export const ORIGEN_MOVILIDAD_JATSA = 'jatsa_movilidad';

function codigoDeConceptoPlame(concepto) {
  return String(
    concepto?.codigoPlame
    || concepto?.codigo
    || concepto?.codigo_plame
    || concepto?.plame_configuracion?.codigo_plame
    || ''
  );
}

function origenConceptoPlano(concepto) {
  return String(
    concepto?.origenCalculo
    || concepto?.origen_calculo
    || concepto?.origen
    || ''
  ).toLowerCase();
}

export function origenEsTareo(concepto) {
  const origen = origenConceptoPlano(concepto);
  return origen === 'tareo' || origen.includes('tareo');
}

function destinoAjusteVaAPlame(concepto) {
  const dest = concepto?.destino_ajuste ?? concepto?.destino;
  if (dest == null || dest === '') return false;
  return String(dest).toLowerCase() !== 'interna';
}

/**
 * Solo la 0909 de Jatsa/ajustes destino PLAME: se informa y no entra al pago.
 * La 0909 de tareo o ficha (sin destino consideraciones) sí se paga.
 */
export function esConceptoDeclaracionMovilidadJatsa(concepto) {
  if (!concepto || esConceptoLiquidacionPlanilla(concepto)) return false;
  if (origenEsTareo(concepto)) return false;
  const origen = origenConceptoPlano(concepto);
  if (origen === ORIGEN_MOVILIDAD_JATSA || origen.includes('jatsa_movilidad')) return true;
  if (codigoDeConceptoPlame(concepto) !== CODIGO_PLAME_MOVILIDAD_JATSA) return false;
  if (concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria) return true;
  return destinoAjusteVaAPlame(concepto);
}

/** Ingreso informado en planilla/REM que no forma parte del neto a pagar. */
export function esConceptoSoloDeclaracion(concepto) {
  if (!concepto || esConceptoLiquidacionPlanilla(concepto)) return false;
  if (esConceptoViaticoPagado(concepto)) return false;
  if (concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria) return true;
  return esConceptoDeclaracionGratificacion(concepto)
    || esConceptoDeclaracionViatico(concepto)
    || esConceptoDeclaracionMovilidadJatsa(concepto);
}

function destinoViaticoVaAPlame(concepto) {
  const dest = String(concepto?.destino_ajuste || concepto?.destino || 'plame').toLowerCase();
  return dest !== 'interna';
}

function codigoDeViatico(concepto) {
  return codigoDeConceptoPlame(concepto);
}

/**
 * 0917 de Jatsa/ajustes destino PLAME: se informa en la boleta oficial
 * y no entra al pago. Si el tareo ya lo trajo, se conserva/actualiza el monto.
 */
export function upsertViaticoDeclaracionEnIngresosPlame(ingresos = [], ajustes = [], extras = {}) {
  const out = Array.isArray(ingresos) ? [...ingresos] : [];
  const extraCampos = typeof extras.ingresoSinDescuentoLicGoceHaber === 'function'
    ? extras.ingresoSinDescuentoLicGoceHaber()
    : {};

  (ajustes || []).forEach((concepto) => {
    if (!esConceptoDeclaracionViatico(concepto) && codigoDeViatico(concepto) !== '0917') {
      return;
    }
    if (!destinoViaticoVaAPlame(concepto)) return;
    const monto = parseFloat(concepto.monto) || 0;
    if (monto <= 0) return;
    const codigo = codigoDeViatico(concepto) || '0917';
    const plameId = concepto.plame_configuracion?.id
      || concepto.plame_configuracion_id
      || concepto.id
      || null;
    const descripcion = concepto.plame_configuracion?.descripcion
      || concepto.descripcion
      || concepto.concepto
      || concepto.nombre
      || 'CONDICIONES DE TRABAJO';
    const fila = {
      codigoPlame: codigo,
      codigo,
      codigo_plame: codigo,
      concepto: descripcion,
      descripcion,
      id: plameId,
      monto,
      plame_configuracion_id: plameId,
      tipo_concepto: 'ingreso',
      origenCalculo: 'viatico',
      origen_calculo: 'viatico',
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
      destino_ajuste: 'plame',
      ...extraCampos,
    };
    const idx = out.findIndex((ing) => String(ing.codigoPlame || ing.codigo || ing.codigo_plame || '') === codigo);
    if (idx >= 0) {
      out[idx] = { ...out[idx], ...fila, monto };
      return;
    }
    out.push(fila);
  });

  return out;
}

/**
 * 0909 de Jatsa (consideraciones destino PLAME): se informa y no entra al pago.
 * No pisa una 0909 que ya venía del tareo / ficha pagada.
 */
export function upsertMovilidadJatsaDeclaracionEnIngresosPlame(ingresos = [], ajustes = [], extras = {}) {
  const out = Array.isArray(ingresos) ? [...ingresos] : [];
  const extraCampos = typeof extras.ingresoSinDescuentoLicGoceHaber === 'function'
    ? extras.ingresoSinDescuentoLicGoceHaber()
    : {};

  (ajustes || []).forEach((concepto) => {
    if (codigoDeConceptoPlame(concepto) !== CODIGO_PLAME_MOVILIDAD_JATSA) return;
    if (!destinoAjusteVaAPlame(concepto)) return;
    if (origenEsTareo(concepto)) return;
    const monto = parseFloat(concepto.monto) || 0;
    if (monto <= 0) return;
    const plameId = concepto.plame_configuracion?.id
      || concepto.plame_configuracion_id
      || concepto.id
      || null;
    const descripcion = concepto.plame_configuracion?.descripcion
      || concepto.descripcion
      || concepto.concepto
      || concepto.nombre
      || 'MOVILIDAD SUPEDITADA A ASISTENCIA Y QUE CUBRE SÓLO EL TRASLADO';
    const fila = {
      codigoPlame: CODIGO_PLAME_MOVILIDAD_JATSA,
      codigo: CODIGO_PLAME_MOVILIDAD_JATSA,
      codigo_plame: CODIGO_PLAME_MOVILIDAD_JATSA,
      concepto: descripcion,
      descripcion,
      id: plameId,
      monto,
      plame_configuracion_id: plameId,
      tipo_concepto: 'ingreso',
      origenCalculo: ORIGEN_MOVILIDAD_JATSA,
      origen_calculo: ORIGEN_MOVILIDAD_JATSA,
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
      destino_ajuste: 'plame',
      ...extraCampos,
    };
    const idx = out.findIndex((ing) => {
      if (codigoDeConceptoPlame(ing) !== CODIGO_PLAME_MOVILIDAD_JATSA) return false;
      if (origenEsTareo(ing)) return false;
      return esConceptoDeclaracionMovilidadJatsa(ing)
        || Boolean(ing.excluirBoletaOrdinaria || ing.excluir_boleta_ordinaria);
    });
    if (idx >= 0) {
      out[idx] = { ...out[idx], ...fila, monto };
      return;
    }
    out.push(fila);
  });

  return out;
}

export function filtrarConceptosBoletaOrdinaria(conceptos = []) {
  return (conceptos || []).filter((c) => !esConceptoSoloDeclaracion(c));
}

export function separarIngresosPlanillaYDeclaracion(ingresos = []) {
  const lista = ingresos || [];
  const planilla = [];
  const declaracionGrati = [];
  const declaracionViatico = [];
  lista.forEach((c) => {
    if (esConceptoDeclaracionViatico(c)) {
      declaracionViatico.push(c);
    } else if (esConceptoDeclaracionGratificacion(c)) {
      declaracionGrati.push(c);
    } else if (esConceptoDeclaracionMovilidadJatsa(c) || esConceptoSoloDeclaracion(c)) {
      declaracionViatico.push(c);
    } else {
      planilla.push(c);
    }
  });
  const sum = (arr) => arr.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const declaracion = [...declaracionGrati, ...declaracionViatico];
  return {
    planilla,
    declaracion,
    declaracionGrati,
    declaracionViatico,
    totalPlanilla: sum(planilla),
    totalDeclaracion: sum(declaracion),
    totalDeclaracionGrati: sum(declaracionGrati),
    totalDeclaracionViatico: sum(declaracionViatico),
  };
}

/**
 * Empleado listo para boleta ordinaria: sin conceptos solo-declaración
 * (gratificaciones / viáticos) y totales recalculados sin esos montos.
 * Conceptos 0406/0313 cargados manualmente se conservan.
 */
function listarConceptosBoletaGrupo(empleado, tipo) {
  const grouped = empleado?.conceptos?.[tipo];
  if (Array.isArray(grouped) && grouped.length) return grouped;
  const sep = empleado?.conceptos_separados?.[tipo];
  if (Array.isArray(sep) && sep.length) return sep;
  const flat = Array.isArray(empleado?.conceptos) ? empleado.conceptos : [];
  const needle = tipo === 'ingresos' ? 'ingreso' : tipo === 'descuentos' ? 'descuento' : 'aporte';
  return flat.filter((c) => String(c.tipo_concepto || '').toLowerCase().includes(needle));
}

export function empleadoParaBoletaOrdinaria(empleado) {
  if (!empleado) return empleado;
  const ingresos = filtrarConceptosBoletaOrdinaria(listarConceptosBoletaGrupo(empleado, 'ingresos'));
  const descuentos = listarConceptosBoletaGrupo(empleado, 'descuentos');
  const aportes = listarConceptosBoletaGrupo(empleado, 'aportes');
  const totalIngresos = ingresos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalDescuentos = descuentos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalAportes = aportes.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);

  const next = {
    ...empleado,
    conceptos: {
      ...empleado.conceptos,
      ingresos,
      descuentos,
      aportes,
    },
    totales: {
      ...(empleado.totales || {}),
      totalIngresos,
      totalDescuentos,
      totalAportes,
      netoAPagar: totalIngresos - totalDescuentos,
    },
  };

  // Misma exclusión en boleta interna (viáticos/grati con destino interna)
  if (empleado.conceptosInternos || empleado.totalesInternos) {
    const ingresosInt = filtrarConceptosBoletaOrdinaria(empleado.conceptosInternos?.ingresos || []);
    const descuentosInt = empleado.conceptosInternos?.descuentos || [];
    const aportesInt = empleado.conceptosInternos?.aportes || [];
    const ti = ingresosInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const td = descuentosInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const ta = aportesInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    next.conceptosInternos = {
      ...(empleado.conceptosInternos || {}),
      ingresos: ingresosInt,
      descuentos: descuentosInt,
      aportes: aportesInt,
    };
    next.totalesInternos = {
      ...(empleado.totalesInternos || {}),
      totalIngresos: ti,
      totalDescuentos: td,
      totalAportes: ta,
      netoAPagar: ti - td,
    };
  }

  return next;
}

export function conceptosSoloDeclaracionGratificacion(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionGratificacion);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionGratificacion);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

export function conceptosSoloDeclaracionViatico(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionViatico);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionViatico);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

export function conceptosSoloDeclaracionMovilidad(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionMovilidadJatsa);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionMovilidadJatsa);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

const sumarConceptos = (arr = []) =>
  (arr || []).reduce((s, c) => s + (parseFloat(c.monto ?? c.valor) || 0), 0);

function destinoSoloInterna(concepto) {
  return String(concepto?.destino_ajuste || concepto?.destino || '').toLowerCase() === 'interna';
}

/** Solo ingesta/ajustes a PLAME. Un 0917 sin destino no es viático PLAME (suele ser interna). */
function destinoDeclaracionPlame(concepto) {
  const dest = String(concepto?.destino_ajuste || concepto?.destino || '').toLowerCase();
  return dest === 'plame' || dest === 'ambas';
}

/** Un monto por código (el mayor > 0). Evita 0917/0909 duplicados en dual/cierre. */
function sumarUnicoPorCodigo(lista, pred) {
  const porCodigo = new Map();
  (lista || []).forEach((c) => {
    if (!pred(c)) return;
    const monto = parseFloat(c?.monto ?? c?.valor) || 0;
    if (monto <= 0) return;
    const codigo = codigoDeConceptoPlame(c) || textoConceptoPlano(c) || 'decl';
    const prev = porCodigo.get(codigo) || 0;
    if (monto > prev) porCodigo.set(codigo, monto);
  });
  return [...porCodigo.values()].reduce((s, n) => s + n, 0);
}

/** Lista de tareos del empleado (vista abierta o cerrada). */
function obtenerTareosEmpleado(empleado) {
  if (!empleado) return [];
  if (Array.isArray(empleado.tareosEmpleadoFiltrados) && empleado.tareosEmpleadoFiltrados.length) {
    return empleado.tareosEmpleadoFiltrados;
  }
  if (Array.isArray(empleado.tareosEmpleado) && empleado.tareosEmpleado.length) {
    return empleado.tareosEmpleado;
  }
  if (Array.isArray(empleado.tareos?.tareosDetallados)) {
    return empleado.tareos.tareosDetallados;
  }
  if (Array.isArray(empleado.tareos)) {
    return empleado.tareos;
  }
  return [];
}

/**
 * Viáticos netamente desde tareos, separados por tipo_boleta.
 * - interna → solo Interna
 * - plame → solo PLAME
 * - (ambas se guardan como dos filas, una de cada tipo)
 * No usa conceptos de boleta calculada (evita PLAME fantasma).
 */
export function montosViaticosDesdeTareos(empleado) {
  let viaticosPlame = 0;
  let viaticosInterna = 0;
  obtenerTareosEmpleado(empleado).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      if (!esConceptoDeclaracionViatico(c)) return;
      const monto = parseFloat(c.monto) || 0;
      if (monto <= 0) return;
      if (conceptoTareoAplicaACanal(c, 'interna', tareo.conceptos, empleado)) {
        viaticosInterna += monto;
      }
      if (conceptoTareoAplicaACanal(c, 'plame', tareo.conceptos, empleado)) {
        viaticosPlame += monto;
      }
    });
  });
  return {
    viaticosPlame,
    viaticosInterna,
  };
}

/**
 * Montos de declaración separados por boleta (evita sumar PLAME+interna dos veces).
 * Viáticos PLAME: solo 0917 con destino plame/ambas (ingesta). Si no, tareo canal PLAME.
 * Un 0917 en ingresos PLAME sin destino es fantasma de interna y no se cuenta.
 * Movilidad: solo 0909 de Jatsa (no la 0909 pagada de tareo).
 */
export function montosDeclaracionPorBoleta(empleado) {
  const viaTareo = montosViaticosDesdeTareos(empleado);
  const viaPlameBoleta = sumarUnicoPorCodigo(
    empleado?.conceptos?.ingresos,
    (c) => esConceptoDeclaracionViatico(c) && destinoDeclaracionPlame(c),
  );
  const viaInternaBoleta = sumarUnicoPorCodigo(
    empleado?.conceptosInternos?.ingresos,
    esConceptoDeclaracionViatico,
  );
  return {
    gratiPlame: sumarConceptos(conceptosSoloDeclaracionGratificacion(empleado, 'plame')),
    gratiInterna: sumarConceptos(conceptosSoloDeclaracionGratificacion(empleado, 'interna')),
    viaticosPlame: viaPlameBoleta > 0 ? viaPlameBoleta : viaTareo.viaticosPlame,
    viaticosInterna: viaInternaBoleta > 0 ? viaInternaBoleta : viaTareo.viaticosInterna,
    movilidadPlame: sumarUnicoPorCodigo(
      empleado?.conceptos?.ingresos,
      (c) => esConceptoDeclaracionMovilidadJatsa(c) && !destinoSoloInterna(c),
    ),
    movilidadInterna: sumarUnicoPorCodigo(
      empleado?.conceptosInternos?.ingresos,
      esConceptoDeclaracionMovilidadJatsa,
    ),
  };
}

/**
 * Suma montos de viáticos / grati de un empleado.
 * Viáticos: desde tareos por destino. Grati: desde conceptos de boleta.
 * @param {'ambas'|'plame'|'interna'} boleta
 */
export function totalesDeclaracionEmpleado(empleado, boleta = 'ambas') {
  const decl = montosDeclaracionPorBoleta(empleado);
  const grati = conceptosSoloDeclaracionGratificacion(empleado, boleta);
  let totalViaticos = 0;
  let totalMovilidad = 0;
  if (boleta === 'plame') {
    totalViaticos = decl.viaticosPlame;
    totalMovilidad = decl.movilidadPlame;
  } else if (boleta === 'interna') {
    totalViaticos = decl.viaticosInterna;
    totalMovilidad = decl.movilidadInterna;
  } else {
    totalViaticos = decl.viaticosPlame + decl.viaticosInterna;
    totalMovilidad = decl.movilidadPlame + decl.movilidadInterna;
  }
  return {
    totalGratificaciones: sumarConceptos(grati),
    totalViaticos,
    totalMovilidad,
    empleadosConGrati: grati.length > 0 ? 1 : 0,
    empleadosConViatico: totalViaticos > 0 ? 1 : 0,
    empleadosConMovilidad: totalMovilidad > 0 ? 1 : 0,
  };
}

/**
 * Filas para Excel: conceptos informativos (grati / viáticos) que no entran al neto.
 * @param {object[]} empleadosCompletos - con conceptos (sin filtrar boleta ordinaria)
 * @param {'plame'|'interna'} boleta
 */
export function filasConceptosNoIngresanAlPago(empleadosCompletos = [], boleta = 'plame') {
  const filas = [];
  (empleadosCompletos || []).forEach((emp) => {
    const ingresos =
      boleta === 'interna'
        ? emp?.conceptosInternos?.ingresos || []
        : emp?.conceptos?.ingresos || [];
    (ingresos || []).forEach((c) => {
      if (!esConceptoSoloDeclaracion(c)) return;
      const monto = parseFloat(c.monto) || 0;
      if (monto <= 0) return;
      const codigo = String(c.codigoPlame || c.codigo || c.codigo_plame || '');
      const descripcion = c.descripcion || c.concepto || c.nombre || codigo || 'Sin descripción';
      const tipo = esConceptoDeclaracionViatico(c)
        ? 'Viáticos'
        : (esConceptoDeclaracionGratificacion(c)
          ? 'Gratificación'
          : (esConceptoDeclaracionMovilidadJatsa(c) ? 'Movilidad' : 'Solo declaración'));
      filas.push({
        dni: emp.documento || emp.numero_documento || '',
        empleado: emp.nombre || emp.nombre_completo || '',
        tipo,
        codigo,
        descripcion,
        monto,
      });
    });
  });
  return filas;
}

/**
 * Marca/inyecta 0406-0313 del módulo de gratificaciones como declaración PLAME
 * (no boleta). También inyecta la grati alternativa en conceptosInternos.
 * No toca liquidación ni conceptos manuales ajenos al módulo.
 */
export function aplicarDeclaracionGratificacionDesdeModulo(empleado) {
  if (!empleado) return empleado;
  const gratificaciones = Array.isArray(empleado.gratificaciones_planilla)
    ? empleado.gratificaciones_planilla
    : [];
  if (gratificaciones.length === 0) return empleado;

  const porCodigo = new Map();
  const porCodigoInterna = new Map();
  gratificaciones.forEach((grat) => {
    (grat.calculos_conceptos || []).forEach((cc) => {
      const codigo = String(cc.codigo_plame || '');
      const monto = parseFloat(cc.monto) || 0;
      if (!codigo || monto <= 0) return;
      porCodigo.set(codigo, {
        codigo,
        monto,
        descripcion: cc.descripcion || codigo,
        id: cc.id,
      });
    });
    (grat.calculos_conceptos_interna || []).forEach((cc) => {
      const codigo = String(cc.codigo_plame || '');
      const monto = parseFloat(cc.monto) || 0;
      if (!codigo || monto <= 0) return;
      porCodigoInterna.set(codigo, {
        codigo,
        monto,
        descripcion: cc.descripcion || codigo,
        id: cc.id,
      });
    });
  });

  let next = empleado;

  if (porCodigo.size > 0) {
    const ingresosActuales = [...(next.conceptos?.ingresos || [])];
    const ingresos = ingresosActuales.map((ing) => {
      if (esConceptoLiquidacionPlanilla(ing)) return ing;
      const codigo = String(ing.codigoPlame || ing.codigo || '');
      if (!porCodigo.has(codigo)) return ing;
      return {
        ...ing,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origen: ing.origen || 'gratificacion_declaracion',
      };
    });

    const codigosPresentes = new Set(
      ingresos.map((ing) => String(ing.codigoPlame || ing.codigo || ''))
    );
    porCodigo.forEach((dato, codigo) => {
      if (codigosPresentes.has(codigo)) return;
      ingresos.push({
        id: dato.id || `grati_${codigo}`,
        codigo,
        descripcion: dato.descripcion,
        monto: dato.monto,
        codigoPlame: codigo,
        base_calculo: 0,
        tipo_concepto: 'ingreso',
        origen: 'gratificacion_declaracion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
      });
    });

    next = {
      ...next,
      conceptos: {
        ...next.conceptos,
        ingresos,
      },
    };
  }

  if (porCodigoInterna.size > 0) {
    const baseInternos = next.conceptosInternos || { ingresos: [], descuentos: [], aportes: [] };
    const ingresosInternosActuales = [...(baseInternos.ingresos || [])];
    const ingresosInternos = ingresosInternosActuales.map((ing) => {
      if (esConceptoLiquidacionPlanilla(ing)) return ing;
      const codigo = String(ing.codigoPlame || ing.codigo || '');
      if (!porCodigoInterna.has(codigo)) return ing;
      return {
        ...ing,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origen: ing.origen || 'gratificacion_declaracion_interna',
      };
    });

    const codigosInternosPresentes = new Set(
      ingresosInternos.map((ing) => String(ing.codigoPlame || ing.codigo || ''))
    );
    porCodigoInterna.forEach((dato, codigo) => {
      if (codigosInternosPresentes.has(codigo)) return;
      ingresosInternos.push({
        id: dato.id || `grati_int_${codigo}`,
        codigo,
        descripcion: dato.descripcion,
        monto: dato.monto,
        codigoPlame: codigo,
        base_calculo: 0,
        tipo_concepto: 'ingreso',
        origen: 'gratificacion_declaracion_interna',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
      });
    });

    next = {
      ...next,
      conceptosInternos: {
        ...baseInternos,
        ingresos: ingresosInternos,
      },
    };
  }

  return next;
}
